/** Standard page structure: eyebrow (workspace) · title · optional one-line description · ONE primary action · secondary actions (lower emphasis). */
import { Link } from "react-router-dom";

export function PageHeader({ eyebrow, title, description, primary, secondary, children, testid = "page-header" }) {
  return (
    <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between mb-4" data-testid={testid}>
      <div className="min-w-0">
        {eyebrow && <div className="text-[10px] font-semibold tracking-[0.12em] uppercase text-[color:var(--st-text-muted)]">{eyebrow}</div>}
        <h1 className="text-xl md:text-2xl font-semibold tracking-tight text-[color:var(--st-text)] truncate" data-testid={`${testid}-title`}>{title}</h1>
        {description && <p className="text-[13px] text-[color:var(--st-text-muted)] mt-0.5 max-w-3xl">{description}</p>}
        {children}
      </div>
      {(primary || secondary) && (
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {secondary}
          {primary}
        </div>
      )}
    </header>
  );
}

export function PrimaryAction({ onClick, to, icon: Icon, children, testid, disabled }) {
  const cls = "os-lift h-9 px-4 rounded-md bg-[color:var(--st-accent)] text-white text-[13px] font-semibold inline-flex items-center gap-1.5 hover:brightness-110 disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--st-accent)]";
  if (to) return <Link to={to} className={cls} data-testid={testid}>{Icon && <Icon className="h-4 w-4 os-ico" aria-hidden="true" />}{children}</Link>;
  return <button type="button" onClick={onClick} disabled={disabled} className={cls} data-testid={testid}>{Icon && <Icon className="h-4 w-4 os-ico" aria-hidden="true" />}{children}</button>;
}

export function SecondaryAction({ onClick, to, icon: Icon, children, testid, title }) {
  const cls = "os-lift h-9 px-3 rounded-md border border-[color:var(--st-border)] bg-white text-[13px] font-medium text-[color:var(--st-text)] inline-flex items-center gap-1.5 hover:bg-[color:var(--st-surface-alt)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[color:var(--st-accent)]";
  if (to) return <Link to={to} className={cls} data-testid={testid} title={title}>{Icon && <Icon className="h-4 w-4 os-ico" aria-hidden="true" />}{children}</Link>;
  return <button type="button" onClick={onClick} className={cls} data-testid={testid} title={title}>{Icon && <Icon className="h-4 w-4 os-ico" aria-hidden="true" />}{children}</button>;
}

/** Single-row section tabs; never wraps into multiple rows (scrolls horizontally on small screens). */
export function SectionTabs({ tabs, value, onChange, testidPrefix = "tab" }) {
  return (
    <div role="tablist" className="flex gap-1 border-b border-[color:var(--st-border)] mb-4 overflow-x-auto os-scroll whitespace-nowrap">
      {tabs.map(([k, label, Icon, badge]) => (
        <button key={k} role="tab" aria-selected={value === k} onClick={() => onChange(k)} data-testid={`${testidPrefix}-${k}`}
          className={"h-10 px-3 text-[13px] inline-flex items-center gap-1.5 border-b-2 -mb-px shrink-0 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[color:var(--st-accent)] " + (value === k ? "border-[color:var(--st-accent)] font-semibold text-[color:var(--st-text)]" : "border-transparent text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]")}>
          {Icon && <Icon className="h-3.5 w-3.5" />}{label}
          {badge != null && badge !== 0 && <span className="ml-0.5 rounded-full bg-rose-100 text-rose-800 text-[10px] font-semibold px-1.5 py-px">{badge}</span>}
        </button>
      ))}
    </div>
  );
}

/** Honest state blocks. */
function EmptyArt({ tone }) {
  const c = { neutral: "#94a3b8", attention: "#d97706", error: "#e11d48", success: "#059669" }[tone] || "#94a3b8";
  return (
    <svg width="72" height="48" viewBox="0 0 72 48" aria-hidden="true" className="mx-auto mb-2 block">
      <rect x="6" y="10" width="60" height="30" rx="4" fill="none" stroke={c} strokeWidth="1.5" opacity="0.7" />
      <line x1="14" y1="19" x2="44" y2="19" stroke={c} strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
      <line x1="14" y1="26" x2="34" y2="26" stroke={c} strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
      <line x1="14" y1="33" x2="38" y2="33" stroke={c} strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
      {tone === "success" ? <path d="M50 27l4 4 8-9" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /> : <circle cx="56" cy="28" r="6" fill="none" stroke={c} strokeWidth="1.5" opacity="0.6" />}
    </svg>
  );
}

export function StateBlock({ tone = "neutral", title, body, action, testid, art = true }) {
  const tones = { neutral: "border-[color:var(--st-border)] bg-white text-[color:var(--st-text-muted)]", attention: "border-amber-200 bg-amber-50 text-amber-900", error: "border-rose-200 bg-rose-50 text-rose-900", success: "border-emerald-200 bg-emerald-50 text-emerald-900" };
  return (
    <div className={`rounded-lg border p-5 text-center ${tones[tone]}`} role={tone === "error" ? "alert" : undefined} data-testid={testid}>
      {art && <EmptyArt tone={tone} />}
      <div className="text-[14px] font-semibold text-[color:var(--st-text)]">{title}</div>
      {body && <div className="text-[13px] mt-1 max-w-xl mx-auto">{body}</div>}
      {action && <div className="mt-3 inline-flex">{action}</div>}
    </div>
  );
}
